import { readStored, removeStored, writeStored } from './storage';
import { getSupabase, SUPABASE_ENABLED } from './supabase';

export type SavedArtist = { id: string; name: string; imageUrl: string | null; createdAt: string };

export interface SavedArtistsRepo {
  list(userId: string): Promise<SavedArtist[]>;
  add(userId: string, artist: SavedArtist): Promise<void>;
  addMany(userId: string, artists: SavedArtist[]): Promise<void>;
  remove(userId: string, artistId: string): Promise<void>;
  updateImage(userId: string, artistId: string, imageUrl: string | null): Promise<void>;
}

const supabaseRepo: SavedArtistsRepo = {
  async list(userId) {
    const { data, error } = await (await getSupabase())
      .from('saved_artists')
      .select('spotify_artist_id, name, image_url, created_at')
      .eq('user_id', userId);
    if (error) throw error;
    return data.map((r) => ({ id: r.spotify_artist_id, name: r.name, imageUrl: r.image_url, createdAt: r.created_at }));
  },
  async add(userId, a) {
    await supabaseRepo.addMany(userId, [a]);
  },
  async addMany(userId, artists) {
    // Upsert keeps a double-click or a second device from failing on the primary key.
    const { error } = await (await getSupabase()).from('saved_artists').upsert(
      artists.map((a) => ({ user_id: userId, spotify_artist_id: a.id, name: a.name, image_url: a.imageUrl, created_at: a.createdAt })),
      { onConflict: 'user_id,spotify_artist_id', ignoreDuplicates: true },
    );
    if (error) throw error;
  },
  async remove(userId, artistId) {
    const { error } = await (await getSupabase()).from('saved_artists').delete().eq('user_id', userId).eq('spotify_artist_id', artistId);
    if (error) throw error;
  },
  async updateImage(userId, artistId, imageUrl) {
    const { error } = await (await getSupabase())
      .from('saved_artists')
      .update({ image_url: imageUrl })
      .eq('user_id', userId)
      .eq('spotify_artist_id', artistId);
    if (error) throw error;
  },
};

// Demo mode: same contract, stored in this browser. Lets the flow run without Supabase.
const isSavedList = (v: unknown): v is SavedArtist[] =>
  Array.isArray(v) && v.every((x) => x && typeof x === 'object' && typeof (x as SavedArtist).id === 'string');
const demoKey = (userId: string) => `demoSaved.v1:${userId}`;
const demoRepo: SavedArtistsRepo = {
  async list(userId) {
    return readStored(demoKey(userId), [], isSavedList);
  },
  async add(userId, a) {
    await demoRepo.addMany(userId, [a]);
  },
  async addMany(userId, artists) {
    const cur = readStored(demoKey(userId), [], isSavedList);
    const fresh = artists.filter((a) => !cur.some((x) => x.id === a.id));
    if (fresh.length) writeStored(demoKey(userId), [...cur, ...fresh]);
  },
  async remove(userId, artistId) {
    writeStored(
      demoKey(userId),
      readStored(demoKey(userId), [], isSavedList).filter((x) => x.id !== artistId),
    );
  },
  async updateImage(userId, artistId, imageUrl) {
    writeStored(
      demoKey(userId),
      readStored(demoKey(userId), [], isSavedList).map((x) => (x.id === artistId ? { ...x, imageUrl } : x)),
    );
  },
};

export function clearDemoData(userId: string) {
  removeStored(demoKey(userId));
}

export const savedRepo: SavedArtistsRepo = SUPABASE_ENABLED ? supabaseRepo : demoRepo;
